import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface GoonghapIntroProps {
  onStart: (nameA: string, nameB: string) => void;
}

export default function GoonghapIntro({ onStart }: GoonghapIntroProps) {
  const [nameA, setNameA] = useState('');
  const [nameB, setNameB] = useState('');
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const canStart = nameA.trim().length > 0 && nameB.trim().length > 0;

  return (
    <div className="min-h-dvh flex flex-col bg-white">
      {/* 헤더 */}
      <header className="flex items-center gap-3 px-6 pt-8 pb-4">
        <button
          onClick={() => navigate('/')}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-gray-700" />
        </button>
        <img
          src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
          alt="바른사주"
          className="h-7 object-contain"
        />
      </header>

      <main className="flex-1 flex flex-col px-6 pt-6 pb-10 max-w-sm mx-auto w-full">
        {/* 타이틀 */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 bg-rose-50 text-rose-600 px-3 py-1.5 rounded-full mb-4 text-xs font-semibold">
            <i className="ri-heart-3-fill text-sm" />
            {isEn ? 'Precision Compatibility Analysis' : '천문학 기반 정밀 궁합'}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-snug mb-3">
            {isEn ? (
              <>Discover the true<br />compatibility between you.</>
            ) : (
              <>두 사람의 진짜<br />궁합을 알아봐요.</>
            )}
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed">
            {isEn ? (
              <>
                Not just zodiac signs. We precisely correct both birth charts, then cross-analyze
                <strong className="text-gray-700"> elements, clashes & harmonies, and major luck cycles</strong> to reveal true compatibility.
              </>
            ) : (
              <>
                단순한 띠 궁합이 아니에요. 두 사람의 사주를 정밀 보정한 뒤,
                <strong className="text-gray-700"> 오행·합충·대운</strong>을 교차 분석해 진짜 궁합을 알려드려요.
              </>
            )}
          </p>
        </div>

        {/* 분석 항목 미리보기 */}
        <div className="grid grid-cols-5 gap-1.5 mb-8">
          {isEn ? [
            { icon: '🔥', label: 'Personality' },
            { icon: '💬', label: 'Comm.' },
            { icon: '💰', label: 'Wealth' },
            { icon: '💖', label: 'Affection' },
            { icon: '⏳', label: 'Timing' },
          ] : [
            { icon: '🔥', label: '성향' },
            { icon: '💬', label: '소통' },
            { icon: '💰', label: '재물' },
            { icon: '💖', label: '애정' },
            { icon: '⏳', label: '타이밍' },
          ].map(item => (
            <div key={item.label} className="flex flex-col items-center bg-gray-50 rounded-xl py-3 gap-1">
              <span className="text-lg">{item.icon}</span>
              <span className="text-xs text-gray-500 font-medium">{item.label}</span>
            </div>
          ))}
        </div>

        {/* 이름 입력 */}
        <div className="space-y-4 mb-8">
          <div>
            <label className="text-xs text-gray-400 font-medium mb-1.5 block">
              {isEn ? 'First Person' : '첫 번째 분'}
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <input
                type="text"
                value={nameA}
                onChange={e => setNameA(e.target.value)}
                maxLength={8}
                placeholder={isEn ? 'Name or nickname (e.g. Alex)' : '이름 또는 별명 (예: 민준)'}
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 focus:bg-white transition-all"
              />
            </div>
          </div>

          <div className="flex items-center justify-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-heart-3-fill text-rose-400 text-base" />
            </div>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <div>
            <label className="text-xs text-gray-400 font-medium mb-1.5 block">
              {isEn ? 'Second Person' : '두 번째 분'}
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-rose-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">B</span>
              </div>
              <input
                type="text"
                value={nameB}
                onChange={e => setNameB(e.target.value)}
                maxLength={8}
                placeholder={isEn ? 'Name or nickname (e.g. Jamie)' : '이름 또는 별명 (예: 지아)'}
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 focus:bg-white transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex-1" />

        <button
          onClick={() => canStart && onStart(nameA.trim(), nameB.trim())}
          disabled={!canStart}
          className={`w-full font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 transition-all whitespace-nowrap ${
            canStart
              ? 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95 cursor-pointer'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isEn ? 'Start Compatibility Analysis' : '궁합 분석 시작하기'}
          <i className="ri-arrow-right-line text-sm" />
        </button>
        <p className="text-center text-xs text-gray-300 mt-3">
          {isEn ? "Enter Person A's info first, then Person B's" : 'A님 정보 먼저, B님 정보 순서로 입력해요'}
        </p>
      </main>
    </div>
  );
}
